import { Suspense, lazy, useEffect, useState } from "react";
import { Play, Loader2 } from "lucide-react";
import { useRealtimeRun } from "@trigger.dev/react-hooks";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  listTasks,
  triggerTask,
  type TaskSummary,
  type TriggerResponse,
  ApiError,
} from "@/lib/api";

// Monaco is heavy (~600 KB raw); lazy-load to keep cold starts cheap.
const MonacoEditor = lazy(() =>
  import("@monaco-editor/react").then((m) => ({ default: m.default }))
);

const SAMPLE_PAYLOADS: Record<string, string> = {
  "local-chat": JSON.stringify(
    { message: "Say hi from the playground.", model: "qwen" },
    null,
    2
  ),
  "shell-exec": JSON.stringify({ command: "uname -a" }, null, 2),
};

export function PlaygroundPage() {
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [selected, setSelected] = useState<TaskSummary | null>(null);
  const [payload, setPayload] = useState<string>("{}");
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [activeRun, setActiveRun] = useState<TriggerResponse | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const list = await listTasks();
        setTasks(list);
        if (list.length > 0 && !selected) {
          setSelected(list[0]);
          setPayload(SAMPLE_PAYLOADS[list[0]!.taskId] ?? "{}");
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load tasks");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pick(t: TaskSummary) {
    setSelected(t);
    setPayload(SAMPLE_PAYLOADS[t.taskId] ?? "{}");
    setActiveRun(null);
    setError(null);
  }

  async function fire() {
    if (!selected) return;
    setError(null);
    setTriggering(true);
    setActiveRun(null);
    try {
      const parsed = JSON.parse(payload);
      const result = await triggerTask({
        taskId: selected.taskId,
        payload: parsed,
      });
      setActiveRun(result);
    } catch (e) {
      if (e instanceof ApiError) {
        const reason =
          (e.body as { reason?: string })?.reason ?? `${e.status}`;
        setError(`Denied: ${reason}`);
      } else if (e instanceof SyntaxError) {
        setError(`Invalid JSON: ${e.message}`);
      } else {
        setError(e instanceof Error ? e.message : "Trigger failed");
      }
    } finally {
      setTriggering(false);
    }
  }

  return (
    <div className="container py-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold">Playground</h1>
        <p className="text-sm text-muted-foreground">
          Trigger tasks against your runners. Live output streams below.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="space-y-2">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Tasks
          </div>
          {tasks.length === 0 && (
            <div className="text-sm text-muted-foreground">No tasks loaded.</div>
          )}
          {tasks.map((t) => (
            <button
              key={t.taskId}
              onClick={() => pick(t)}
              className={
                "w-full rounded-md border p-3 text-left text-sm transition-colors hover:bg-muted " +
                (selected?.taskId === t.taskId ? "border-primary bg-muted" : "")
              }
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{t.displayName}</span>
                <TierBadge tier={t.tier} />
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {t.project} · {t.taskId}
              </div>
            </button>
          ))}
        </aside>

        <main className="min-w-0 space-y-4">
          {selected && (
            <div className="rounded-lg border bg-card">
              <div className="flex items-center justify-between border-b px-4 py-2">
                <div>
                  <div className="text-sm font-medium">
                    {selected.displayName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {selected.description}
                  </div>
                </div>
                <Button onClick={fire} disabled={triggering}>
                  {triggering ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Triggering…
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Trigger
                    </>
                  )}
                </Button>
              </div>
              <div className="h-[300px]">
                <Suspense
                  fallback={
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      Loading editor…
                    </div>
                  }
                >
                  <MonacoEditor
                    language="json"
                    theme="vs-dark"
                    value={payload}
                    onChange={(v) => setPayload(v ?? "")}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      tabSize: 2,
                      formatOnPaste: true,
                      scrollBeyondLastLine: false,
                    }}
                  />
                </Suspense>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {activeRun && (
            <RunPanel
              runId={activeRun.runId}
              accessToken={activeRun.publicAccessToken}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function TierBadge({ tier }: { tier: TaskSummary["tier"] }) {
  const variant: Parameters<typeof Badge>[0]["variant"] =
    tier === "safe"
      ? "success"
      : tier === "side-effect"
        ? "info"
        : tier === "destructive"
          ? "warning"
          : "destructive";
  return <Badge variant={variant}>{tier}</Badge>;
}

function RunPanel({ runId, accessToken }: { runId: string; accessToken: string }) {
  const { run, error } = useRealtimeRun(runId, { accessToken });

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Run
          </span>
          <span className="font-mono text-xs">{runId}</span>
        </div>
        <StatusBadge status={run?.status} />
      </div>
      <div className="p-4 text-sm">
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
            {error.message}
          </div>
        )}
        {!run && !error && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Waiting for runner…
          </div>
        )}
        {run?.output != null && (
          <pre className="overflow-auto rounded-md bg-muted p-3 font-mono text-xs">
            {JSON.stringify(run.output, null, 2)}
          </pre>
        )}
        {run?.error && (
          <pre className="overflow-auto rounded-md border border-destructive/40 bg-destructive/10 p-3 font-mono text-xs text-destructive">
            {JSON.stringify(run.error, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return <Badge variant="outline">pending</Badge>;
  const variant: Parameters<typeof Badge>[0]["variant"] =
    status === "COMPLETED"
      ? "success"
      : status === "FAILED" || status === "CANCELED"
        ? "destructive"
        : status === "EXECUTING"
          ? "info"
          : "outline";
  return <Badge variant={variant}>{status.toLowerCase()}</Badge>;
}
